<template>
  <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
    <div class="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
      <div>
        <p class="text-sm font-semibold text-gray-900 dark:text-white">Bookings Over Time</p>
        <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Created, completed, and canceled per day</p>
      </div>
      <div class="flex items-center gap-4">
        <span class="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <span class="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block"></span> Created
        </span>
        <span class="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <span class="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block"></span> Completed
        </span>
        <span class="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <span class="w-2.5 h-2.5 rounded-full bg-red-400 inline-block"></span> Canceled
        </span>
      </div>
    </div>

    <!-- Skeleton -->
    <div v-if="loading && !data" class="animate-pulse p-5 h-56 flex flex-col justify-end gap-2">
      <div class="flex items-end gap-1 h-40">
        <div v-for="i in 14" :key="i" class="flex-1 bg-gray-100 dark:bg-gray-800 rounded-t"
          :style="{ height: (20 + Math.cos(i) * 20 + i * 2) + '%' }"></div>
      </div>
      <div class="flex gap-1">
        <div v-for="i in 7" :key="i" class="flex-1 h-2 bg-gray-100 dark:bg-gray-800 rounded"></div>
      </div>
    </div>

    <!-- Empty -->
    <div v-else-if="isEmpty" class="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div class="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
        <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
        </svg>
      </div>
      <p class="text-sm font-semibold text-gray-700 dark:text-gray-200">No bookings yet</p>
      <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">Booking trends will appear here once you receive bookings.</p>
    </div>

    <!-- Chart -->
    <div v-else ref="chartEl" class="w-full h-56"></div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useAnalyticsBookingsStore } from 'stores/analyticsBookings.js'

const props = defineProps({ duration: { type: String, required: true } })
const store = useAnalyticsBookingsStore()

const chartEl = ref(null)
const loading  = computed(() => store.bookingsOverTimeLoading)
const data     = computed(() => store.bookingsOverTime[props.duration] ?? null)
const isEmpty  = computed(() => {
  if (!data.value) return false
  return (data.value.created ?? []).every(v => v === 0) &&
         (data.value.completed ?? []).every(v => v === 0) &&
         (data.value.canceled ?? []).every(v => v === 0)
})

let chart = null

function renderChart() {
  if (!data.value || !chartEl.value) return
  if (chart) { chart.destroy(); chart = null }

  const { labels, created, completed, canceled } = data.value
  chart = Highcharts.chart(chartEl.value, {
    chart:  { type: 'area', height: 224, spacing: [16, 16, 16, 16] },
    xAxis:  {
      categories: labels,
      tickInterval: Math.ceil(labels.length / 7),
      crosshair: { color: '#e5e7eb', width: 1 },
    },
    yAxis:  { min: 0, allowDecimals: false },
    tooltip: { shared: true, crosshairs: true },
    plotOptions: {
      area: {
        fillOpacity: 0.07,
        marker: { enabled: false, symbol: 'circle', radius: 3, states: { hover: { enabled: true } } },
        lineWidth: 2,
      },
    },
    series: [
      { name: 'Created',   data: created,   color: '#2563eb', fillColor: 'rgba(37,99,235,0.07)'   },
      { name: 'Completed', data: completed, color: '#10b981', fillColor: 'rgba(16,185,129,0.07)'  },
      { name: 'Canceled',  data: canceled,  color: '#ef4444', fillColor: 'rgba(239,68,68,0.07)'   },
    ],
  })
}

watch(data, (val) => { if (val) nextTick(renderChart) })
onMounted(() => { if (data.value) renderChart() })
onUnmounted(() => { if (chart) { chart.destroy(); chart = null } })
</script>
